import React, { useState, useEffect, useId } from "react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Card, CardHeader, CardContent } from "../../../../components/ui/card";
import { useCheckoutContext } from "../CheckoutContext";
import { CHECKOUT_PRIMARY_CTA_CLASS } from "./checkoutPaymentLabels";
import { getPromoAppliedMessage } from "./checkoutPromoLabels";

const MAX_PROMO_LENGTH = 32;

type PromotionalCodeVariant = "card" | "compact";

interface PromotionalCodeProps {
  variant?: PromotionalCodeVariant;
}

export const PromotionalCode: React.FC<PromotionalCodeProps> = ({ variant = "card" }) => {
  const {
    state,
    setPromoCode,
    applyPromoCode,
    formatPercentage,
    formatCurrency,
  } = useCheckoutContext();
  const [inputValue, setInputValue] = useState(state.promoCode.code);
  const sectionId = useId();
  const headingId = useId();
  const inputId = useId();
  const errorId = useId();
  const hintId = useId();

  useEffect(() => {
    setInputValue(state.promoCode.code);
  }, [state.promoCode.code]);

  const isBusy = state.isApplyingPromoCode || state.isApplyingVoucher;
  const orderNotReady = !state.orderId || state.isInitializingOrder;
  const canApply =
    inputValue.trim() !== "" && !isBusy && !orderNotReady && !state.promoCode.applied;

  const syncCodeToState = () => {
    const normalized = inputValue.trim().toUpperCase();
    if (normalized && normalized !== state.promoCode.code) {
      setPromoCode(normalized);
    }
  };

  const handleApply = async () => {
    const normalized = inputValue.trim().toUpperCase();
    if (!normalized) return;
    setInputValue(normalized);
    await applyPromoCode(normalized);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && canApply) {
      e.preventDefault();
      void handleApply();
    }
  };

  const appliedMessage = getPromoAppliedMessage(state.promoCode, {
    formatCurrency,
    formatPercentage,
  });

  const appliedBlock = (
    <div
      className="rounded-md border border-success bg-success-muted p-4"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <svg
          className="mt-0.5 h-5 w-5 shrink-0 text-success"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="min-w-0">
          <p className="font-semibold text-success-subtle">Cupom aplicado</p>
          <p className="mt-1 font-mono text-base font-medium text-success-subtle break-all">
            {state.promoCode.code}
          </p>
          <p className="mt-1 text-sm text-success-subtle">{appliedMessage}</p>
        </div>
      </div>
    </div>
  );

  const applyLabel = isBusy ? "Aplicando…" : "Aplicar";

  const promoInput = (
    <Input
      type="text"
      id={inputId}
      name="promo-code"
      placeholder="Ex.: VERAO2026"
      value={inputValue}
      onChange={(e) => {
        const next = e.target.value.toUpperCase();
        setInputValue(next);
        if (!state.promoCode.applied) {
          setPromoCode(next);
        }
      }}
      onBlur={syncCodeToState}
      onKeyDown={handleKeyDown}
      disabled={isBusy || orderNotReady}
      maxLength={MAX_PROMO_LENGTH}
      autoComplete="off"
      spellCheck={false}
      aria-labelledby={headingId}
      aria-invalid={Boolean(state.promoCode.error)}
      aria-describedby={
        [state.promoCode.error ? errorId : null, orderNotReady ? hintId : null]
          .filter(Boolean)
          .join(" ") || undefined
      }
      className="h-11 min-h-11 min-w-0 w-full flex-1 uppercase"
      style={{ textTransform: "uppercase" }}
    />
  );

  const formHints = (
    <>
      {orderNotReady ? (
        <p id={hintId} className="text-sm text-text-secondary">
          Preparando seu pedido — o cupom fica disponível em instantes.
        </p>
      ) : null}
      {state.promoCode.error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {state.promoCode.error}
        </p>
      ) : null}
    </>
  );

  const formBlock =
    variant === "compact" ? (
      <div className="space-y-1">
        {promoInput}
        <button
          type="button"
          onClick={() => void handleApply()}
          disabled={!canApply}
          aria-busy={isBusy}
          className="inline-flex min-h-11 items-center rounded-sm !rounded-none !border-transparent !bg-transparent !px-0 text-sm font-medium text-success-subtle hover:!border-transparent hover:!bg-transparent hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:text-text-secondary disabled:no-underline"
        >
          {applyLabel}
        </button>
        {formHints}
      </div>
    ) : (
      <div className="space-y-2">
        <div className="flex max-w-lg flex-col gap-2 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">{promoInput}</div>
          <Button
            type="button"
            onClick={() => void handleApply()}
            disabled={!canApply}
            isLoading={isBusy}
            aria-busy={isBusy}
            className={`min-h-11 w-full shrink-0 px-5 sm:w-auto sm:min-w-[7.5rem] ${CHECKOUT_PRIMARY_CTA_CLASS}`}
          >
            {applyLabel}
          </Button>
        </div>
        {formHints}
      </div>
    );

  const body = state.promoCode.applied ? appliedBlock : formBlock;

  if (variant === "compact") {
    return (
      <section id={sectionId} aria-labelledby={headingId}>
        <h3 id={headingId} className="text-sm font-medium text-foreground">
          Código promocional
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-text-secondary">
          Tem um cupom? Aplique antes de gerar o pagamento.
        </p>
        <div className="mt-3">{body}</div>
      </section>
    );
  }

  return (
    <Card id={sectionId} className="border shadow-none">
      <CardHeader className="space-y-1.5 px-6 pb-0 pt-6">
        <h2 id={headingId} className="text-balance text-xl font-semibold text-foreground">
          Código promocional
        </h2>
        <p className="text-sm text-text-secondary">
          O desconto vale para este pacote e aparece no total antes de você pagar.
        </p>
      </CardHeader>
      <CardContent className="px-6 pb-6 pt-4">{body}</CardContent>
    </Card>
  );
};
